<template>
  <div class="agent-list-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-user"></i>
              代理列表
            </h1>
            <p class="page-subtitle">管理代理信息、审核状态和运营数据</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 统计卡片 -->
      <div class="stats-section">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon">
                <i class="el-icon-user"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ agentStats.total }}</div>
                <div class="stat-label">代理总数</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon active">
                <i class="el-icon-success"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ agentStats.approved }}</div>
                <div class="stat-label">已通过</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon warning">
                <i class="el-icon-time"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ pendingApplicationsCount }}</div>
                <div class="stat-label">待审核</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon error">
                <i class="el-icon-error"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ agentStats.rejected }}</div>
                <div class="stat-label">已拒绝</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>
      
      <!-- 搜索和筛选区域 -->
      <div class="filter-section">
        <div class="filter-card">
          <el-form :model="searchForm" :inline="true" class="filter-form">
            <div class="form-row">
              <div class="form-item">
                <label class="form-label">
                  <i class="el-icon-search"></i>
                  搜索代理
                  <span class="search-tip">（支持手机号精确查询）</span>
                </label>
                <el-input
                  v-model="searchForm.keyword"
                  placeholder="输入代理名称/手机号进行搜索"
                  clearable
                  size="large"
                  @keyup.enter.native="handleSearch"
                >
                  <i slot="prefix" class="el-input__icon el-icon-search"></i>
                </el-input>
              </div>
              
              <div class="form-item">
                <label class="form-label">审核状态</label>
                <el-select v-model="searchForm.status" placeholder="全部状态" clearable size="large">
                  <el-option label="待审核" :value="0">
                    <span style="color: #e6a23c">
                      <i class="el-icon-time"></i> 待审核
                    </span>
                  </el-option>
                  <el-option label="已通过" :value="1">
                    <span style="color: #67c23a">
                      <i class="el-icon-success"></i> 已通过
                    </span>
                  </el-option>
                  <el-option label="已拒绝" :value="2">
                    <span style="color: #f56c6c">
                      <i class="el-icon-error"></i> 已拒绝
                    </span>
                  </el-option>
                  <el-option label="已冻结" :value="3">
                    <span style="color: #909399">
                      <i class="el-icon-lock"></i> 已冻结
                    </span>
                  </el-option>
                </el-select>
              </div>
              
              <div class="form-item">
                <label class="form-label">省份</label>
                <el-input
                  v-model="searchForm.province"
                  placeholder="请输入省份"
                  clearable
                  size="large"
                />
              </div>
              
              <div class="form-item">
                <label class="form-label">城市</label>
                <el-input
                  v-model="searchForm.city"
                  placeholder="请输入城市"
                  clearable
                  size="large"
                />
              </div>
            </div>
            
            <div class="form-actions">
              <el-button type="primary" @click="handleSearch" size="large">
                <i class="el-icon-search"></i> 搜索
              </el-button>
              <el-button @click="resetSearch" size="large">
                <i class="el-icon-refresh-left"></i> 重置
              </el-button>
            </div>
          </el-form>
        </div>
      </div>
      
      <!-- 代理列表 -->
      <div class="table-section">
        <el-card class="table-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="header-title">
              <i class="el-icon-user"></i>
              <span>代理列表</span>
            </div>
            <div class="header-actions">
              <el-button 
                size="small" 
                @click="refreshData"
                icon="el-icon-refresh"
                :loading="loading"
              >
                刷新
              </el-button>
            </div>
          </div>
          
          <el-table
            :data="agentList"
            style="width: 100%"
            :loading="loading"
            stripe
            :header-cell-style="{background:'#f5f7fa', color:'#606266', fontWeight: 'bold'}"
            @sort-change="handleSortChange"
          >
            <el-table-column label="代理信息" min-width="300">
              <template slot-scope="scope">
                <div class="agent-info-cell">
                  <div class="agent-avatar">
                    <i class="el-icon-user"></i>
                  </div>
                  <div class="agent-details">
                    <div class="agent-name">{{ scope.row.store_name }}</div>
                    <div class="agent-contact">联系人: {{ scope.row.contact_person }}</div>
                    <div class="agent-phone">电话: {{ scope.row.contact_phone }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="地址信息" min-width="200">
              <template slot-scope="scope">
                <div class="address-info">
                  <div class="address-region">
                    <i class="el-icon-location"></i>
                    {{ scope.row.province }} {{ scope.row.city }} {{ scope.row.district }}
                  </div>
                  <div class="address-detail">{{ scope.row.address || '未填写详细地址' }}</div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="代理标签" width="120" align="center">
          <template slot-scope="scope">
                <div class="agent-tag-column">
                  <el-tag v-if="scope.row.store_tag" type="warning" size="small" effect="plain">
                    {{ scope.row.store_tag }}
                  </el-tag>
                  <span v-else class="no-tag-text">-</span>
                </div>
          </template>
        </el-table-column>

            <el-table-column label="状态" width="120" align="center">
          <template slot-scope="scope">
                <el-tag 
                  :type="getStatusType(scope.row.status)" 
                  size="medium"
                >
              {{ getStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>

            <el-table-column label="证书编号" width="140" align="center">
          <template slot-scope="scope">
                <div class="certificate-info">
                  <div v-if="scope.row.certificate_no" class="certificate-no-display">
                    {{ scope.row.certificate_no }}
                  </div>
                  <div v-else class="no-certificate">
                    <span class="no-cert-text">待颁发证书</span>
                  </div>
                </div>
          </template>
        </el-table-column>

            <el-table-column label="创建时间" width="160" align="center">
          <template slot-scope="scope">
                <div class="time-info">
                  <div class="create-date">{{ scope.row.created_at | formatDate }}</div>
                </div>
          </template>
        </el-table-column>

            <el-table-column label="操作" width="300" fixed="right" align="center">
          <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button 
                    size="mini" 
                    icon="el-icon-view" 
                    @click="handleDetail(scope.row)"
                    title="查看详情"
                  >
                    详情
                  </el-button>
            <el-button
                    v-if="scope.row.status === 0" 
                    size="mini" 
                    type="success" 
                    @click="handleApprove(scope.row)"
                    title="通过审核"
                  >
                    通过
            </el-button>
            <el-button
                    v-if="scope.row.status === 0" 
                    size="mini" 
                    type="danger" 
                    @click="handleReject(scope.row)"
                    title="拒绝申请"
                  >
                    拒绝
                  </el-button>
                  <el-button
                    v-if="scope.row.status === 1"
                    size="mini"
                    type="success"
                    icon="el-icon-document"
                    @click="handleCertificate(scope.row)"
                    title="生成授权证书"
                  >
                    证书
                  </el-button>
                  <el-dropdown v-if="scope.row.status !== 0" @command="handleCommand($event, scope.row)" trigger="click">
                    <el-button size="mini" type="primary">
                      更多 <i class="el-icon-arrow-down"></i>
            </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="edit">编辑</el-dropdown-item>
                      <el-dropdown-item command="delete" class="delete-item">删除代理</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
          </template>
        </el-table-column>
          </el-table>
          
          <!-- 分页 -->
          <div class="pagination-wrapper">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="pagination.total">
            </el-pagination>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { getStoreList, updateStoreStatus, deleteStore, getStoreApplicationList } from '@/api/store'

export default {
  name: 'AgentList',
  
  data() {
    return {
      loading: false,
      agentList: [],
      pendingApplicationsCount: 0, // 待审核申请数量
      agentStats: {
        total: 0,        // 代理总数
        approved: 0,     // 已通过数量
        rejected: 0      // 已拒绝数量
      },
      searchForm: {
        keyword: '',
        status: '',
        province: '',
        city: ''
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      }
    }
  },
  
  filters: {
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  },
  
  async mounted() {
    await this.loadAgentList()
    await this.fetchPendingApplications()
    await this.fetchAgentStats()
  },
  
  methods: {
    async loadAgentList() {
      try {
        this.loading = true
        
        const params = {
          store_type: 2, // 固定为代理类型
          status: this.searchForm.status !== '' ? this.searchForm.status : '',
          province: this.searchForm.province || '',
          city: this.searchForm.city || '',
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        }
        
        // 智能检测搜索关键字类型
        if (this.searchForm.keyword && this.searchForm.keyword.trim()) {
          const keyword = this.searchForm.keyword.trim()
          
          // 检测是否为手机号（中国大陆手机号格式：11位，以1开头，第二位为3-9）
          if (/^1[3-9]\d{9}$/.test(keyword)) {
            // 使用手机号参数进行精确匹配
            params.phone = keyword
          } else {
            // 使用关键字参数进行模糊匹配
            params.keyword = keyword
          }
        }
        
        // 清理空参数
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })
        
        const response = await getStoreList(params)
        
        if (response.code === 200) {
          this.agentList = response.data.list || []
          this.pagination.total = response.data.pagination?.total || 0
        } else {
          this.$message.error(response.message || '获取代理列表失败')
        }
      } catch (error) {
        console.error('获取代理列表失败:', error)
        this.$message.error('获取代理列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取待审核申请数量
    async fetchPendingApplications() {
      try {
        const response = await getStoreApplicationList({
          status: 0, // 待审核状态
          page_size: 1 // 只需要获取总数，不需要具体数据
        })
        this.pendingApplicationsCount = response.data?.pagination?.total || 0
      } catch (error) {
        console.error('获取待审核申请数量失败:', error)
        this.pendingApplicationsCount = 0
      }
    },
    
    // 获取代理各状态统计数据
    async fetchAgentStats() {
      try {
        // 获取总代理数
        const totalResponse = await getStoreList({
          store_type: 2,  // 代理类型
          page_size: 1
        })
        this.agentStats.total = totalResponse.data?.pagination?.total || 0
        
        // 获取已通过代理数
        const approvedResponse = await getStoreList({
          store_type: 2,  // 代理类型
          status: 1,
          page_size: 1
        })
        this.agentStats.approved = approvedResponse.data?.pagination?.total || 0
        
        // 获取已拒绝代理数
        const rejectedResponse = await getStoreList({
          store_type: 2,  // 代理类型
          status: 2,
          page_size: 1
        })
        this.agentStats.rejected = rejectedResponse.data?.pagination?.total || 0
        
      } catch (error) {
        console.error('获取代理统计数据失败:', error)
      }
    },
    
    // 综合刷新数据
    async refreshData() {
      await this.loadAgentList()
      await this.fetchPendingApplications()
      await this.fetchAgentStats()
    },
    
    handleSearch() {
      this.pagination.page = 1
      this.loadAgentList()
    },
    
    resetSearch() {
      this.searchForm = {
        keyword: '',
        status: '',
        province: '',
        city: ''
      }
      this.pagination.page = 1
      this.loadAgentList()
    },
    
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.page = 1
      this.loadAgentList()
    },
    
    handleCurrentChange(val) {
      this.pagination.page = val
      this.loadAgentList()
    },
    
    handleSortChange(column) {
      // 处理排序逻辑
    },
    
    handleDetail(row) {
      this.$router.push(`/admin/stores/${row.id}`)
    },
    
    handleCertificate(store) {
      // 跳转到证书生成页面，传递代理信息
      this.$router.push({
        path: '/admin/system/certificates',
        query: {
          storeId: store.id,
          storeName: store.store_name,
          contactPerson: store.contact_person,
          contactPhone: store.contact_phone,
          address: store.address
        }
      })
    },
    
    async handleCommand(command, store) {
      switch (command) {
        case 'edit':
          this.$router.push(`/admin/stores/${store.id}?tab=edit`)
          break
        case 'delete':
          await this.handleDeleteStore(store)
          break
      }
    },
    
    // 删除代理
    async handleDeleteStore(store) {
      try {
        const { value } = await this.$prompt(
          '请输入删除原因（此操作不可逆，将影响关联的用户绑定、商品数据等）',
          '删除代理确认',
          {
            confirmButtonText: '确认删除',
            cancelButtonText: '取消',
            confirmButtonClass: 'el-button--danger',
            inputPlaceholder: '请输入删除原因',
            inputValidator: (value) => {
              if (!value || value.trim().length < 5) {
                return '删除原因至少需要5个字符'
              }
              return true
            },
            inputErrorMessage: '删除原因不能为空且至少需要5个字符'
          }
        )
        
        if (value && value.trim()) {
          const secondConfirm = await this.$confirm(
            `确认删除代理"${store.store_name}"吗？此操作将：\n- 永久删除代理基本信息\n- 解除绑定到该代理的用户\n- 标记该代理创建的商品为已删除\n\n删除原因：${value.trim()}`,
            '最终确认',
            {
              confirmButtonText: '确认删除',
              cancelButtonText: '取消',
              type: 'error',
              confirmButtonClass: 'el-button--danger'
            }
          )
          
          if (secondConfirm !== 'cancel') {
            await deleteStore(store.id, {
              reason: value.trim()
            })
            
            this.$message.success('代理已删除')
            this.refreshData()
          }
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除代理失败:', error)
          this.$message.error('删除代理失败')
        }
      }
    },
    
    async handleApprove(row) {
      try {
        const { value } = await this.$prompt('请输入通过审核的原因（可选）:', '审核代理申请', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPlaceholder: '通过原因（可选）'
        }).catch(() => ({ value: '' }))
        
        await updateStoreStatus(row.id, {
          status: 1,
          reason: value || ''
        })
        
        this.$message.success('代理审核通过')
        this.refreshData()
      } catch (error) {
        console.error('审核通过失败:', error)
        this.$message.error('审核通过失败')
      }
    },
    
    async handleReject(row) {
      try {
        const { value } = await this.$prompt('请输入拒绝审核的原因:', '审核代理申请', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputValidator: (value) => {
            if (!value || value.trim() === '') {
              return '拒绝原因不能为空'
            }
            return true
          },
          inputErrorMessage: '请输入拒绝原因'
        })
        
        await updateStoreStatus(row.id, {
          status: 2,
          reason: value
        })
        
        this.$message.success('代理申请已拒绝')
        this.refreshData()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('审核拒绝失败:', error)
          this.$message.error('审核拒绝失败')
        }
      }
    },
    
    
    
    getStatusType(status) {
      const statusMap = {
        0: 'warning',  // 待审核
        1: 'success',  // 已通过
        2: 'danger',   // 已拒绝
        3: 'info'      // 已冻结
      }
      return statusMap[status] || 'info'
    },
    
    getStatusText(status) {
      const statusMap = {
        0: '待审核',
        1: '已通过',
        2: '已拒绝',  
        3: '已冻结'
      }
      return statusMap[status] || '未知'
    }
  }
}
</script>

<style scoped>
.agent-list-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions .el-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.header-actions .el-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

/* 主要内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 30px;
}

/* 筛选区域 */
.filter-section {
  margin-bottom: 30px;
}

.filter-card {
  background: white;
  border-radius: 16px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #f1f3f4;
}

.filter-form {
  margin: 0;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-item {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: #606266;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-label i {
  color: #409eff;
}

.search-tip {
  font-size: 12px;
  color: #909399;
  font-weight: 400;
}

.form-actions {
  text-align: center;
}

.form-actions .el-button {
  min-width: 120px;
  margin: 0 10px;
}

/* 统计卡片 */
.stats-section {
  margin-bottom: 25px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
  position: relative;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
}

.stat-icon i {
  color: #4facfe;
  z-index: 1;
}

.stat-icon.active::before {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.active i {
  color: #67c23a;
}

.stat-icon.warning::before {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.stat-icon.warning i {
  color: #e6a23c;
}

.stat-icon.error::before {
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.stat-icon.error i {
  color: #f093fb;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 表格区域 */
.table-section {
  margin-bottom: 25px;
}

.table-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-count {
  font-size: 14px;
  color: #909399;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding: 30px 0;
}

/* 代理信息样式 */
.store-info {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 8px 0;
}

.store-avatar {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.store-details {
  flex: 1;
}

.store-name {
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
  font-size: 15px;
  line-height: 1.4;
}

.store-contact {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.store-phone {
  font-size: 12px;
  color: #666;
}

.store-tag {
  margin-top: 4px;
}

/* 地址信息样式 */
.address-info {
  padding: 8px 0;
}

.address-region {
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.address-region i {
  color: #409eff;
}

.address-detail {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

/* 时间信息样式 */
.time-info {
  padding: 8px 0;
  text-align: center;
}

.create-date {
  font-size: 14px;
  color: #303133;
  margin-bottom: 4px;
}

.certificate-no {
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
}

/* 证书编号列样式 */
.certificate-info {
  text-align: center;
  padding: 8px 0;
}

.certificate-no-display {
  font-size: 13px;
  color: #303133;
  font-family: 'Courier New', monospace;
  background: #e8f4f8;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
  border: 1px solid #b3d8ff;
}

.no-certificate {
  padding: 4px 0;
}

.no-cert-text {
  font-size: 12px;
  color: #909399;
  background: #f5f7fa;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.add-placeholder-btn {
  margin-top: 10px;
  color: #409eff;
}

/* 代理标签列样式 */
.agent-tag-column {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 32px;
}

.no-tag-text {
  color: #c0c4cc;
  font-style: italic;
  font-size: 13px;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 3px;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  width: 100%;
}

.action-buttons .el-button {
  margin: 0;
  padding: 5px 8px;
  font-size: 12px;
  min-width: auto;
}

.action-buttons .el-button--mini {
  padding: 5px 8px;
  font-size: 12px;
  min-width: auto;
}

.action-buttons .el-dropdown .el-button {
  padding: 5px 6px;
}

/* 删除菜单项样式 */
.delete-item {
  color: #f56c6c !important;
}

.delete-item:hover {
  background-color: #fef0f0 !important;
  color: #f56c6c !important;
}

/* 统一样式别名 */
.agent-info-cell {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 8px 0;
}

.agent-avatar {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.agent-details {
  flex: 1;
}

.agent-name {
  font-weight: 600;
  color: #303133;
  margin-bottom: 6px;
  font-size: 15px;
  line-height: 1.4;
}

.agent-contact {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
}

.agent-phone {
  font-size: 12px;
  color: #666;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-title i {
  color: #409eff;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 32px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  padding: 30px 0;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .main-content {
    padding: 0 20px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
  
  .stats-section .el-row {
    display: flex;
    flex-wrap: wrap;
  }
  
  .stats-section .el-col {
    margin-bottom: 15px;
  }
}

@media (max-width: 768px) {
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .main-content {
    padding: 0 15px;
  }
  
  .filter-form .el-col {
    margin-bottom: 15px;
  }
  
  .form-actions {
    justify-content: center;
  }
  
  .stats-section .el-col {
    flex: 0 0 50%;
    max-width: 50%;
  }
  
  .stat-card {
    padding: 20px 16px;
    gap: 12px;
  }
  
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 22px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .action-buttons {
    gap: 2px;
    flex-wrap: wrap;
  }
  
  .action-buttons .el-button {
    font-size: 11px;
    padding: 4px 6px;
  }
}

@media (max-width: 480px) {
  .stats-section .el-col {
    flex: 0 0 100%;
    max-width: 100%;
  }
  
  .store-info {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }
  
  .store-avatar {
    width: 45px;
    height: 45px;
  }
}
</style>
